import { DeliveryZone, DeliveryZoneBase } from '@flipdish/api-client-typescript';
import { createSelector } from 'reselect';

import {
  type DeliveryZonesDraftState,
  type DisplayDeliveryZone,
  initialDraftState,
} from '../models/deliveryZonesDraft.model';
import { createOrmSelector } from './selector';

export type { DisplayDeliveryZone };

export const createDeliveryZonesSelector = (storeId: number | string) =>
  createOrmSelector<DeliveryZone[]>((session) => {
    const zones =
      session.StoreDeliveryZone?.all()
        .filter((zone) => {
          const zoneFlipdishStore = zone.FlipdishStore?.toString();
          return zoneFlipdishStore === storeId;
        })
        .toModelArray()
        .map((zone) => zone.ref) || [];

    return zones;
  });

export const createDeliveryZoneByIdSelector = (deliveryZoneId: number | string) =>
  createOrmSelector<DeliveryZone>((session) => {
    const deliveryZone = session.StoreDeliveryZone.withId(deliveryZoneId.toString());
    return deliveryZone ? deliveryZone.ref : undefined;
  });

export const createDeliveryZoneIdsSelector = (storeId: number | string) => {
  const zonesSelector = createDeliveryZonesSelector(storeId.toString());

  return createSelector([zonesSelector], (zones): number[] => {
    return zones.map((zone) => zone.Id as number);
  });
};

const deliveryZonesDraftSelector = (state: AppState): DeliveryZonesDraftState =>
  (state.deliveryZones.draft ?? initialDraftState) as DeliveryZonesDraftState;

/**
 * Stable synthetic Id for pending creates (negative int, no collision with typical server ids).
 * Same clientId always yields the same id for React keys and MapItem identity.
 */
export const clientIdToStableDisplayId = (clientId: string): number => {
  let hash = 0;
  for (let index = 0; index < clientId.length; index += 1) {
    const charCode = clientId.charCodeAt(index);
    hash = (Math.imul(31, hash) + charCode) | 0;
  }
  if (hash === 0) {
    return -1;
  }
  return hash < 0 ? hash : -Math.abs(hash);
};

const draftAppliesToStore = (draft: DeliveryZonesDraftState, storeId: number | string): boolean =>
  draft.storeId === null || Number(draft.storeId) === Number(storeId);

const pendingCreateToDisplayZone = (
  clientId: string,
  zone: DeliveryZoneBase
): DisplayDeliveryZone => ({
  ...(zone as DeliveryZone),
  Id: clientIdToStableDisplayId(clientId),
  clientId,
  isPendingCreate: true,
});

/**
 * ORM zones for the store, minus pending deletes, overlaid with pendingUpdates,
 * plus pendingCreates as synthetic rows. Ignores draft when it targets another store.
 */
export const createDisplayDeliveryZonesSelector = (storeId: number | string) => {
  const store = storeId.toString();
  const ormZonesSelector = createDeliveryZonesSelector(store);

  return createSelector(
    [ormZonesSelector, deliveryZonesDraftSelector],
    (ormZones, draft): DisplayDeliveryZone[] => {
      if (!draftAppliesToStore(draft, store)) {
        return ormZones as DisplayDeliveryZone[];
      }

      const deleted = new Set(draft.pendingDeletes);
      const fromOrm: DisplayDeliveryZone[] = ormZones
        .filter((zone) => zone.Id != null && !deleted.has(zone.Id as number))
        .map((zone) => {
          const id = zone.Id as number;
          const patch = draft.pendingUpdates[id];
          return (patch ? { ...zone, ...patch } : zone) as DisplayDeliveryZone;
        });

      const fromDraft = draft.pendingCreates.map(({ clientId, zone }) =>
        pendingCreateToDisplayZone(clientId, zone)
      );

      return [...fromOrm, ...fromDraft];
    }
  );
};

export const createDisplayDeliveryZoneIdsSelector = (storeId: number | string) => {
  const displaySelector = createDisplayDeliveryZonesSelector(storeId);

  return createSelector([displaySelector], (zones): number[] =>
    zones.map((zone) => zone.Id as number)
  );
};

export const createDisplayDeliveryZoneByIdSelector = (
  storeId: number | string,
  deliveryZoneId: number | string
) => {
  const displaySelector = createDisplayDeliveryZonesSelector(storeId);
  const idNum = Number(deliveryZoneId);

  return createSelector([displaySelector], (zones): DisplayDeliveryZone | undefined =>
    zones.find((zone) => Number(zone.Id) === idNum)
  );
};
